import { useState } from "react";
import { Button, Card, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { ConfirmDialog } from "../ConfirmDialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import type { Environment } from "../../redux/reducers/environmentSlice";

type OrganizationEnvironmentsCardProps = {
  environments: Environment[];
  onAdd: (name: string, description: string) => void;
  onUpdate: (id: string, data: { name?: string; description?: string | null }) => void;
  onDelete: (id: string) => Promise<void>;
};

export const OrganizationEnvironmentsCard = ({ environments, onAdd, onUpdate, onDelete }: OrganizationEnvironmentsCardProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmedName = newName.trim();
    if (!trimmedName) return;
    onAdd(trimmedName, newDescription.trim());
    setNewName("");
    setNewDescription("");
    setShowAddForm(false);
  };

  const handleStartEdit = (environment: Environment) => {
    setEditingId(environment.id);
    setEditName(environment.name);
    setEditDescription(environment.description ?? "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const handleSaveEdit = (id: string) => {
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    const trimmedDescription = editDescription.trim();
    onUpdate(id, { name: trimmedName, description: trimmedDescription || null });
    handleCancelEdit();
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await onDelete(deleteId);
      setDeleteId(null);
    }
  };

  const sortedEnvironments = [...environments].sort((a, b) => a.name.localeCompare(b.name));
  const environmentToDelete = environments.find((env) => env.id === deleteId);

  return (
    <OrganizationSettingsTab
      title="Environments"
      description="Named deployment targets (e.g. production, staging). Releases and entrypoints reference these."
      count={environments.length}
    >
      <View className="flex flex-col gap-3">
        {sortedEnvironments.map((environment) => (
          <Card
            key={environment.id}
            className="p-4"
          >
            {editingId === environment.id ? (
              <View className="flex flex-col gap-3">
                <InputField
                  label="Name"
                  value={editName}
                  onChange={setEditName}
                  placeholder="e.g. production"
                />
                <InputField
                  label="Description"
                  value={editDescription}
                  onChange={setEditDescription}
                  placeholder="Optional"
                />
                <View className="flex flex-row gap-2 justify-end">
                  <Button
                    variant="neutral"
                    size="sm"
                    outline
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    outline
                    onClick={() => handleSaveEdit(environment.id)}
                    disabled={!editName.trim()}
                  >
                    <Icon
                      name="check"
                      size={14}
                    />
                    Save
                  </Button>
                </View>
              </View>
            ) : (
              <View className="flex flex-row items-center justify-between gap-3">
                <View className="flex flex-row items-center gap-3 min-w-0">
                  <Icon
                    name="rocket"
                    size={16}
                    className="text-gray-400 shrink-0"
                  />
                  <View className="flex flex-col min-w-0">
                    <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{environment.name}</Text>
                    {environment.description && <Text className="text-xs text-gray-400 dark:text-gray-500 truncate">{environment.description}</Text>}
                  </View>
                </View>
                <View className="flex flex-row items-center gap-2 shrink-0">
                  <Button
                    variant="neutral"
                    size="sm"
                    outline
                    onClick={() => handleStartEdit(environment)}
                    className="h-8"
                  >
                    <Icon
                      name="edit"
                      size={14}
                    />
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    outline
                    onClick={() => setDeleteId(environment.id)}
                    className="h-8"
                    aria-label="Delete"
                  >
                    <Icon
                      name="trash"
                      size={14}
                    />
                  </Button>
                </View>
              </View>
            )}
          </Card>
        ))}

        {showAddForm ? (
          <Card className="p-4 border-dashed">
            <View className="flex flex-col gap-3">
              <InputField
                label="Name"
                value={newName}
                onChange={setNewName}
                placeholder="e.g. production"
              />
              <InputField
                label="Description"
                value={newDescription}
                onChange={setNewDescription}
                placeholder="Optional"
              />
              <View className="flex flex-row gap-2 justify-end">
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={() => {
                    setShowAddForm(false);
                    setNewName("");
                    setNewDescription("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  outline
                  onClick={handleAdd}
                  disabled={!newName.trim()}
                >
                  <Icon
                    name="add"
                    size={14}
                  />
                  Add Environment
                </Button>
              </View>
            </View>
          </Card>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex flex-row items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-3 py-2.5 group border border-dashed border-gray-300 dark:border-gray-600"
          >
            <View className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={13}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add environment
            </Text>
          </button>
        )}
      </View>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Environment"
        message={
          environmentToDelete?.name
            ? `Delete "${environmentToDelete.name}"? Releases and entrypoints referencing this environment will break.`
            : "Delete this environment?"
        }
        confirmLabel="Delete"
        variant="danger"
      />
    </OrganizationSettingsTab>
  );
};
